import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { ShieldCheck, BarChart3, Sparkles, Linkedin } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { LINKEDIN_URL } from "@/lib/constants";

const credIcons = [ShieldCheck, BarChart3, Sparkles];

const AboutMarketingSection = () => {
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const a = t.aiTeams.aboutMarketing;
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
  }, []);

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <section className="py-16 bg-card/40">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start">
          {/* Left column - text */}
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {a.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-8">
              {a.heading}
            </h2>

            <div className="space-y-4 mb-10">
              {a.body.map((paragraph, i) => (
                <p
                  key={i}
                  className={
                    i === 0
                      ? "text-lg font-display text-foreground leading-relaxed"
                      : "text-sm font-body text-muted-foreground leading-relaxed"
                  }
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Credibility items */}
            <div className="space-y-5">
              {a.credibility.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: prefersReduced ? 0 : i * 0.1, duration: 0.4 }}
                  className="flex gap-3"
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 flex-shrink-0 mt-0.5">
                    {(() => { const Icon = credIcons[i]; return <Icon size={18} className="text-primary" strokeWidth={1.5} />; })()}
                  </div>
                  <div>
                    <p className="font-body font-semibold text-foreground text-sm">{item.title}</p>
                    <p className="text-xs font-body text-muted-foreground leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right column - photo */}
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.2, ...anim.transition }}
            className="flex flex-col items-center lg:items-start"
          >
            <div className="relative w-full max-w-[340px]">
              {/* Breathing glow */}
              <div
                className="absolute -inset-4 rounded-2xl bg-primary/10 blur-2xl -z-10"
                style={!prefersReduced ? { animation: "glow-breathe 6s ease-in-out infinite" } : undefined}
              />
              <picture>
                <source srcSet="/images/chris.webp" type="image/webp" />
                <img
                  src="/images/chris.jpg"
                  alt={a.photoName}
                  width={615}
                  height={800}
                  className="w-full rounded-2xl border border-primary/15 object-cover"
                  style={{ boxShadow: "0 0 40px -10px rgba(19, 216, 170, 0.2)" }}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div className="mt-4 text-center lg:text-left">
              <p className="font-body font-semibold text-foreground text-sm">{a.photoName}</p>
              <p className="text-xs font-mono text-muted-foreground tracking-wide">{a.photoRole}</p>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-body font-medium text-muted-foreground hover:text-primary transition-colors mt-2"
              >
                <Linkedin size={14} />
                {a.linkedinLabel}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutMarketingSection;
